import React, { useEffect, useState } from "react";
import { getDriverPayoutById } from "../../api/DriverPayoutApi.js";
import { X, Calendar, Clipboard, Wallet, Tag } from "lucide-react";
import { toast } from "../shared/Toast.jsx";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return dateStr;
  }
};

const ViewDriverPayoutModal = ({ id, close }) => {
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPayoutDetails = async () => {
      try {
        setLoading(true);
        const res = await getDriverPayoutById(id);
        if (res.success) {
          setPayout(res.data);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load payout details");
      } finally {
        setLoading(false);
      }
    };
    fetchPayoutDetails();
  }, [id]);

  if (loading) {
    return (
      <div style={backdropStyle}>
        <div style={{ ...modalStyle, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <div style={{ color: "#20B967", fontWeight: 600 }}>Loading details...</div>
        </div>
      </div>
    );
  }

  if (!payout) return null;

  return (
    <div style={backdropStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, color: "#fff", fontSize: "20px", fontWeight: 600 }}>Payout Details</h2>
          <button onClick={close} style={closeBtnStyle}><X size={20} /></button>
        </div>

        <div style={bodyStyle}>
          {/* Driver & Date info */}
          <div style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <Calendar size={18} color="#20B967" />
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#6B7280", textTransform: "uppercase" }}>Driver & Period</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", padding: "12px", background: "#F9FAFB", borderRadius: "8px" }}>
              <div>
                <div style={labelStyle}>Driver Name</div>
                <div style={valStyle}>{payout.driver?.driverName || "N/A"}</div>
              </div>
              <div>
                <div style={labelStyle}>Company</div>
                <div style={valStyle}>{payout.company || payout.companyName || payout.driver?.vehicle?.clientAllotments?.[0]?.client?.clientName || "N/A"}</div>
              </div>
              <div>
                <div style={labelStyle}>Period</div>
                <div style={valStyle}>{formatDate(payout.fromDate)} to {formatDate(payout.toDate)}</div>
              </div>
            </div>
          </div>

          {/* Tripping & Earnings */}
          <div style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <Clipboard size={18} color="#20B967" />
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#6B7280", textTransform: "uppercase" }}>Trips & Earnings</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", padding: "12px", background: "#F9FAFB", borderRadius: "8px" }}>
              <div>
                <div style={labelStyle}>Total Trips</div>
                <div style={valStyle}>{payout.totalTrips}</div>
              </div>
              <div>
                <div style={labelStyle}>Total Amount</div>
                <div style={valStyle}>₹ {payout.totalAmount}</div>
              </div>
            </div>
          </div>

          {/* Deductions breakdown */}
          <div style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <Wallet size={18} color="#EF4444" />
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#6B7280", textTransform: "uppercase" }}>Deductions</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", padding: "12px", background: "#FEF2F2", borderRadius: "8px", border: "1px solid #FEE2E2" }}>
              <div>
                <div style={labelStyle}>Fuel Deduction</div>
                <div style={{ ...valStyle, color: "#991B1B" }}>₹ {payout.fuelDeduction || 0}</div>
              </div>
              <div>
                <div style={labelStyle}>Penalty Deduction</div>
                <div style={{ ...valStyle, color: "#991B1B" }}>₹ {payout.penaltyDeduction || 0}</div>
              </div>
              <div>
                <div style={labelStyle}>Advance Deduction</div>
                <div style={{ ...valStyle, color: "#991B1B" }}>₹ {payout.advanceDeduction || 0}</div>
              </div>
              <div>
                <div style={labelStyle}>Other Deduction</div>
                <div style={{ ...valStyle, color: "#991B1B" }}>₹ {payout.otherDeduction || 0}</div>
              </div>
              <div style={{ gridColumn: "span 2", paddingTop: "8px", borderTop: "1px solid #FCA5A5", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600, color: "#991B1B" }}>Total Deduction</span>
                <span style={{ fontWeight: 700, color: "#991B1B" }}>₹ {payout.totalDeduction || 0}</span>
              </div>
            </div>
          </div>

          {/* Summary / Total Payable */}
          <div style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <Tag size={18} color="#10B981" />
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#6B7280", textTransform: "uppercase" }}>Summary</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", padding: "12px", background: "#ECFDF5", borderRadius: "8px", border: "1px solid #D1FAE5" }}>
              <div>
                <div style={labelStyle}>Status</div>
                <span style={{
                  display: "inline-block",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: payout.status === "Paid" ? "#D1FAE5" : payout.status === "Hold" ? "#FEF3C7" : "#F3F4F6",
                  color: payout.status === "Paid" ? "#065F46" : payout.status === "Hold" ? "#92400E" : "#374151"
                }}>
                  {payout.status}
                </span>
              </div>
              <div>
                <div style={labelStyle}>Payable Amount</div>
                <div style={{ ...valStyle, color: "#065F46", fontSize: "18px", fontWeight: 700 }}>₹ {payout.payableAmount}</div>
              </div>
              {payout.remarks && (
                <div style={{ gridColumn: "span 2", marginTop: "8px" }}>
                  <div style={labelStyle}>Remarks</div>
                  <div style={{ ...valStyle, fontStyle: "italic", fontSize: "13px" }}>{payout.remarks}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={footerStyle}>
          <button onClick={close} style={closeBtnFooterStyle}>Close</button>
        </div>
      </div>
    </div>
  );
};

const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000
};

const modalStyle = {
  background: "#fff",
  width: "550px",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh"
};

const headerStyle = {
  backgroundColor: "#212A30",
  padding: "16px 20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const closeBtnStyle = {
  background: "none",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center"
};

const bodyStyle = {
  padding: "20px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "20px"
};

const sectionStyle = {
  display: "flex",
  flexDirection: "column"
};

const labelStyle = {
  fontSize: "12px",
  color: "#6B7280",
  marginBottom: "4px",
  fontWeight: 500
};

const valStyle = {
  fontSize: "14px",
  color: "#1F2937",
  fontWeight: 600
};

const footerStyle = {
  padding: "12px 20px",
  borderTop: "1px solid #F3F4F6",
  display: "flex",
  justifyContent: "flex-end",
  backgroundColor: "#F9FAFB"
};

const closeBtnFooterStyle = {
  backgroundColor: "#E5E7EB",
  color: "#374151",
  border: "none",
  padding: "8px 16px",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 500
};

export default ViewDriverPayoutModal;
